<template>
   <div
      class="supplies-project-percentage-input"
      :data-grid-section="gridSection"
      :data-grid-table="gridTableIndex"
      :data-grid-row="gridRowIndex"
      :data-grid-col="gridColIndex"
   >
      <FieldInput
         ref="input"
         style="min-width: 6rem; width: 100%; font-size: 12px !important"
         class="mb-0"
         :ident="id"
         append="%"
         :field="displayActivityPercentageField"
         :has-warning="hasWarning"
         :placeholder="placeholder"
         :disabled="disabled"
         fill
         @input="onInput"
         @blur="onBlur"
      />
   </div>
</template>

<script>
import {integer} from 'vuelidate/lib/validators';
import FieldInput from '@/components/forms/FieldInput';
import {hasValueChanged, normalizeNullableInteger} from '@/helpers/expenseInput';

export default {
   components: {
      FieldInput,
   },

   props: {
      gridSection: {type: String, required: false},
      gridTableIndex: {type: Number, required: false},
      gridRowIndex: {type: Number, required: false},
      gridColIndex: {type: Number, required: false},
      ident: {
         type: [String, Number],
      },
      value: {
         type: Number,
         default: null,
      },
      assigneeId: {
         type: String,
         default: '',
      },
      activityId: {
         type: [String, Number],
         default: null,
      },
      summary: {
         type: Object,
         default: null,
      },
      placeholder: {
         type: String,
         default: null,
      },
      disabled: {
         type: Boolean,
         default: false,
      },
      hasWarning: {
         type: Boolean,
         default: false,
      },
      activityIds: {
         type: Array,
         default: () => [],
      },
      expenseType: {
         type: String,
         required: true,
         validator: (value) => ['cloud', 'supplies', 'contractors', 'employees'].includes(value),
      },
   },

   data() {
      return {
         draftActivityPercentage: null,
         hasDraftActivityPercentage: false,
         startingActivityPercentage: null,
      };
   },

   computed: {
      id() {
         return `input-percentage-${this.ident}`.replace(/\s/g, '-');
      },

      activityPercentageField() {
         const summaryValue =
            this.summary && this.activityId
               ? this.summary.activitiesData?.[this.activityId] ?? this.value
               : this.value;
         return {
            value: summaryValue,
            status: '',
            lastUpdated: '',
         };
      },

      displayActivityPercentageField() {
         return {
            ...this.activityPercentageField,
            value: this.hasDraftActivityPercentage
               ? this.draftActivityPercentage
               : this.activityPercentageField.value,
         };
      },

      persistedActivityValue() {
         if (!this.summary || !this.activityId) return this.value;
         return this.summary.activitiesData?.[this.activityId] ?? this.value;
      },
   },

   methods: {
      normalizeInputValue(value) {
         return normalizeNullableInteger(value);
      },

      onInput(newValue) {
         if (!this.hasDraftActivityPercentage) {
            this.startingActivityPercentage = this.normalizeInputValue(this.persistedActivityValue);
         }
         this.hasDraftActivityPercentage = true;
         this.draftActivityPercentage = newValue;
         this.validate(newValue);
      },

      async onBlur(newValue) {
         const normalizedValue = this.normalizeInputValue(newValue);
         const startingValue = this.hasDraftActivityPercentage
            ? this.startingActivityPercentage
            : this.normalizeInputValue(this.persistedActivityValue);

         if (!hasValueChanged(normalizedValue, startingValue)) {
            this.draftActivityPercentage = normalizedValue;
            this.hasDraftActivityPercentage = false;
            this.startingActivityPercentage = null;
            return;
         }

         await this.setActivityPercentage(newValue);
      },

      async setActivityPercentage(newValue) {
         if (!this.validate(newValue)) return;

         newValue = this.normalizeInputValue(newValue);

         this.$emit('input', newValue);
         this.draftActivityPercentage = newValue;
         this.hasDraftActivityPercentage = false;
         this.startingActivityPercentage = null;

         try {
            await this.$store.dispatch(`${this.expenseType}/saveActivityPercentage`, {
               companyId: this.$companyId,
               summary: {
                  ...this.summary,
                  assigneeId: this.assigneeId,
               },
               activityId: this.activityId,
               value: newValue,
            });
         } catch {
            // The store action handles rollback and user alerting.
         }
      },

      /** Validate a percentage */
      validate(value) {
         if (value === '' || value === null) {
            this.$refs.input.setState(null);
            return true;
         }
         if (!integer(value)) {
            this.$refs.input.setState(false, 'Must be an integer');
            return false;
         }
         value = parseInt(value, 10);
         if (value < 0 || value > 100) {
            this.$refs.input.setState(false, 'Must be between 0 and 100');
            return false;
         }
         this.$refs.input.setState(null);
         return true;
      },
   },
};
</script>

<style lang="scss" scoped></style>
